import * as React from 'react';
import { useBreakpoint } from '@resi-media/resi-ui';
import { LAYOUT_CONTAINER_ID } from '@sites/constants/layout';

/* Configuration constants */

// Show header when near top
const MIN_SCROLL_THRESHOLD = 20;
// Delay to prevent too frequent updates during fast scrolling
const DEBOUNCE_DELAY = 100;
// Minimum distance to scroll before changing header visibility
const MIN_SCROLL_DISTANCE = 20;
const TOP_SENTINEL_ID = 'header-visibility-top-sentinel';

/**
 * Hook to control header visibility based on scroll position and direction
 *
 * Returns a boolean that indicates whether the header should be visible
 * - true: Header should be visible
 * - false: Header should be hidden
 *
 * The header is always visible on desktop and when at the top of the page
 */
export const useHeaderVisibility = (containerId = LAYOUT_CONTAINER_ID) => {
  const mediaQuery = useBreakpoint();
  const isDesktop = mediaQuery.xl;

  // Use binary state for better synchronization
  const [headerVisibility, setHeaderVisibility] = React.useState(true);

  // Refs for scroll handling
  const scrollRefs = React.useRef<{
    container: HTMLElement | null;
    lastScrollY: number;
    lastUpdateTime: number;
    raf: number | null;
    retryTimeout: NodeJS.Timeout | null;
    scrollDirection: 'down' | 'up' | null;
    scrollDistance: number;
  }>({
    container: null,
    lastScrollY: 0,
    lastUpdateTime: 0,
    raf: null,
    retryTimeout: null,
    scrollDirection: null,
    scrollDistance: 0,
  });

  // Refs for top position detection
  const topRefs = React.useRef<{ observer: IntersectionObserver | null; sentinel: HTMLElement | null }>({
    observer: null,
    sentinel: null,
  });

  const resetScrollState = React.useCallback((currentScrollY: number, now: number) => {
    scrollRefs.current.lastScrollY = currentScrollY;
    scrollRefs.current.scrollDirection = null;
    scrollRefs.current.scrollDistance = 0;
    scrollRefs.current.lastUpdateTime = now;
    setHeaderVisibility(true);
  }, []);

  const getContainer = React.useCallback(() => {
    return document.getElementById(containerId);
  }, [containerId]);

  // Set up IntersectionObserver to detect when at top of page
  React.useEffect(() => {
    if (isDesktop) {
      return;
    }

    const { current: scrollRefValue } = scrollRefs;
    const { current: topRefValue } = topRefs;

    const initializeTopObserver = () => {
      try {
        const container = getContainer();
        if (!container) {
          scrollRefValue.retryTimeout = setTimeout(initializeTopObserver, 100);
          return;
        }

        let topSentinel = document.getElementById(TOP_SENTINEL_ID);
        if (!topSentinel) {
          topSentinel = document.createElement('div');
          topSentinel.id = TOP_SENTINEL_ID;
          topSentinel.style.height = '1px';
          topSentinel.style.width = '100%';
          topSentinel.style.position = 'absolute';
          topSentinel.style.top = '0';
          topSentinel.style.pointerEvents = 'none';
          container.prepend(topSentinel);
        }

        topRefValue.sentinel = topSentinel;

        topRefValue.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              setHeaderVisibility(true);
            }
          },
          {
            root: container,
            rootMargin: '0px',
            threshold: 0.1,
          }
        );

        topRefValue.observer.observe(topRefValue.sentinel);
      } catch (error) {
        console.error('Error initializing top observer:', error);
      }
    };

    initializeTopObserver();

    return () => {
      if (scrollRefValue.retryTimeout) {
        clearTimeout(scrollRefValue.retryTimeout);
      }

      if (topRefValue.observer && topRefValue.sentinel) {
        topRefValue.observer.unobserve(topRefValue.sentinel);
        topRefValue.observer.disconnect();
      }

      const topSentinel = document.getElementById(TOP_SENTINEL_ID);
      if (topSentinel?.parentNode) {
        topSentinel.parentNode.removeChild(topSentinel);
      }
    };
  }, [containerId, isDesktop, getContainer]);

  // Set up scroll handling
  React.useEffect(() => {
    if (isDesktop) {
      return;
    }

    const { current: scrollRefValue } = scrollRefs;

    const handleScroll = () => {
      // Use requestAnimationFrame for smooth animations
      if (scrollRefValue.raf) {
        cancelAnimationFrame(scrollRefValue.raf);
      }

      scrollRefValue.raf = requestAnimationFrame(() => {
        const now = Date.now();

        // Simple debounce for fast scrolling
        if (now - scrollRefValue.lastUpdateTime < DEBOUNCE_DELAY) {
          return;
        }

        const currentScrollY = scrollRefValue.container?.scrollTop ?? 0;

        // Always fully show header at the very top or near top, but intersection observer as well when debounce prevents this
        if (currentScrollY <= MIN_SCROLL_THRESHOLD) {
          resetScrollState(currentScrollY, now);
          return;
        }

        // Determine scroll direction
        const direction = currentScrollY > scrollRefValue.lastScrollY ? 'down' : 'up';
        // If direction changed, reset distance counter
        if (direction !== scrollRefValue.scrollDirection) {
          scrollRefValue.scrollDirection = direction;
          scrollRefValue.scrollDistance = 0;
        } else {
          // Accumulate scroll distance in the same direction
          scrollRefValue.scrollDistance += Math.abs(currentScrollY - scrollRefValue.lastScrollY);
        }
        // Only change visibility after scrolling a bit in one direction
        if (scrollRefValue.scrollDistance > MIN_SCROLL_DISTANCE) {
          // For scrolling down, hide header
          if (direction === 'down') {
            setHeaderVisibility(false);
          }
          // For scrolling up, show header
          else {
            setHeaderVisibility(true);
          }
          scrollRefValue.lastUpdateTime = now;
        }

        scrollRefValue.lastScrollY = currentScrollY;
      });
    };

    const initializeScrollListener = () => {
      scrollRefValue.container = getContainer();

      if (!scrollRefValue.container) {
        scrollRefValue.retryTimeout = setTimeout(initializeScrollListener, 100);
        return;
      }

      scrollRefValue.container.addEventListener('scroll', handleScroll, { passive: true });
    };

    initializeScrollListener();

    return () => {
      if (scrollRefValue.retryTimeout) {
        clearTimeout(scrollRefValue.retryTimeout);
      }

      if (scrollRefValue.raf) {
        cancelAnimationFrame(scrollRefValue.raf);
      }

      if (scrollRefValue.container) {
        scrollRefValue.container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [containerId, isDesktop, resetScrollState, getContainer]);

  // Check desktop state immediately to prevent flicker
  if (isDesktop) {
    return true;
  }

  return headerVisibility;
};
